import React, { useState } from 'react';
import { User, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const Dashboard = () => {
  const [currentDate, setCurrentDate] = useState(new Date(2025, 11, 1)); // November 2025
  const [activeMenu, setActiveMenu] = useState('Dashboard');

  // Data untuk grafik active users per tanggal
  const activeUsersData = [
    { date: '1 Des', users: 3 },
    { date: '2 Des', users: 14 },
    { date: '3 Des', users: 15 },
    { date: '4 Des', users: 10 },
    { date: '5 Des', users: 12 },
    { date: '6 Des', users: 11 },
    { date: '7 Des', users: 2 },
    { date: '8 Des', users: 13 },
    { date: '9 Des', users: 13 },
    { date: '10 Des', users: 8 },
    { date: '11 Des', users: 16 },
    { date: '12 Des', users: 14 },
    { date: '13 Des', users: 8 },
    { date: '14 Des', users: 3 },
    { date: '15 Des', users: 16 },
    { date: '16 Des', users: 13 },
    { date: '17 Des', users: 9 },
    { date: '18 Des', users: 6 },
    { date: '19 Des', users: 8 },
    { date: '20 Des', users: 4 },
    { date: '21 Des', users: 0 },
    { date: '22 Des', users: 8 },
    { date: '23 Des', users: 6 },
    { date: '24 Des', users: 1 },
    { date: '25 Des', users: 3 },
    { date: '26 Des', users: 1 },
    
  ];

  // Total active users adalah data terakhir
  const totalActiveUsers = activeUsersData.reduce((sum, item) => sum + item.users, 0);

  

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 
                      'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  const dayNames = ['SEN', 'SEL', 'RAB', 'KAM', 'JUM', 'SAB', 'MIN'];

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderCalendarDays = () => {
    const days = [];
    const prevMonthDays = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
    
    // Previous month days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(
        <div key={`prev-${i}`} className="text-center py-2 text-gray-400 text-sm">
          {prevMonthDays - i}
        </div>
      );
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === 26;
      const isWeekend = (startingDayOfWeek + day - 1) % 7 === 0 || (startingDayOfWeek + day - 1) % 7 === 6;
      
      days.push(
        <div 
          key={day} 
          className={`text-center py-2 text-sm ${
            isToday 
              ? 'bg-blue-600 text-white rounded-full font-bold' 
              : isWeekend 
                ? 'text-red-600' 
                : 'text-gray-800'
          }`}
        >
          {day}
        </div>
      );
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push(
        <div key={`next-${day}`} className="text-center py-2 text-gray-400 text-sm">
          {day}
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Dashboard</h1>
          
          {/* Top Row */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Report Active Users */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Report Active Users</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activeUsersData}>
                    <defs>
                      <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '8px 12px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="users" 
                      stroke="#2563eb" 
                      strokeWidth={2}
                      fill="url(#colorUsers)"
                      name="Active Users"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Total Active Users */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Total Active Users</h2>
              <div className="flex flex-col items-center justify-center h-64">
                <User size={80} className="text-blue-600 mb-4" strokeWidth={1.5} />
                <div className="text-5xl font-bold text-gray-800 mb-2">{totalActiveUsers}</div>
                <div className="text-gray-600">Active Users</div>
                
              </div>
            </div>
          </div>
          
          {/* Bottom Row */}
          <div className="grid grid-cols-2 gap-6">
            {/* Maps */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Maps</h2>
              <div className="h-80 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                {/* Placeholder for map */}
                <p className="text-sm">Peta akan ditampilkan di sini</p>
              </div>
            </div>
            
            {/* Calendar */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Calendar</h2>
              
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
                  <ChevronLeft size={16} />
                </button>
                <span className="font-semibold">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </span>
                <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
                  <ChevronRight size={16} />
                </button>
                <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
                  <ChevronRight size={20} />
                </button>
              </div>
              
              {/* Day Names */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {renderCalendarDays()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;